import { DateFormat, DateUtility } from 'utility'

function parseBool(value, fallback = false) {
    if (value === undefined || value === null) return fallback
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
        const v = value.trim().toLowerCase()
        if (['1', 'true', 'yes', 'y'].includes(v)) return true
        if (['0', 'false', 'no', 'n'].includes(v)) return false
    }
    return fallback
}

const firstDefined = (...values) =>
    values.find(
        value =>
            value !== undefined &&
            value !== null &&
            !(typeof value === 'string' && value.trim() === ''),
    ) ?? null

export const SIGNATURE_TYPE = {
    Draw: 1,
    Upload: 2,
    Typed: 3,
}

export const SIGNATURE_TYPE_LABELS = {
    1: 'Drawn',
    2: 'Uploaded',
    3: 'Typed',
}

export const MIN_APPROVAL_NOTES_LENGTH = 5
export const MAX_SIGNATURE_BYTES = 512000

export const APPROVAL_STATUS = {
    Pending: 'pending',
    Approved: 'approved',
    All: 'all',
    AwaitingMe: 'awaiting_me',
    AwaitingOthers: 'awaiting_others',
    Rejected: 'rejected',
}

export const WORKFLOW_TRIGGER = {
    Immediate: 1,
    Delay: 2,
    Manual: 3,
    // 2026-10-22: the approval starts once every finding (auto-ticket) of the inspection is resolved
    AfterFindings: 4,
}

export const REJECTION_POLICY = {
    Anyone: 1,
    FinalOnly: 2,
}

export const REMINDER_CADENCE = {
    Off: 0,
    Daily: 1,
    Every2Days3: 2,
}

export const AUTO_ESCALATE = {
    Off: 0,
    After5Days: 1,
}

export const REJECTION_CATEGORIES = [
    { value: 'missing_evidence', text: 'Missing evidence' },
    { value: 'score_error', text: 'Score error' },
    { value: 'section_incomplete', text: 'Section incomplete' },
    { value: 'other', text: 'Other' },
]

export const EMPTY_APPROVAL_FILTERS = {
    CustomerId: '',
    TemplateId: '',
    UserId: '',
    StartDate: '',
    EndDate: '',
    Search: '',
}

/** Matches backend: IsSuperAdmin || IsAdmin || RoleId == 3 */
export const canApproveReports = user => {
    if (!user) return false

    const caps = user?.Capabilities
    if (caps != null && caps.CanApproveReports != null) {
        return parseBool(caps.CanApproveReports, false)
    }

    if (user.IsSuperAdmin === true) return true
    if (user.IsAdmin === true) return true
    if (Number(user.RoleId ?? user.RoleID) === 3) return true
    const role = String(user.Role || '').toLowerCase()
    if (role === 'admin' || role === 'superadmin' || role === 'super admin') {
        return true
    }
    return false
}

export const canConfigureApprovalWorkflow = user => {
    if (!user) return false

    const caps = user?.Capabilities
    if (caps != null && caps.CanConfigureApprovalWorkflow != null) {
        return parseBool(caps.CanConfigureApprovalWorkflow, false)
    }

    return canApproveReports(user)
}

export const getSignatureTypeLabel = type =>
    SIGNATURE_TYPE_LABELS[Number(type)] || ''

export const getCertificateFileName = (url, fallback = '') => {
    if (url) {
        try {
            const path = String(url).split('?')[0]
            const name = path.substring(path.lastIndexOf('/') + 1)
            if (name) return decodeURIComponent(name)
        } catch {
            // fall through
        }
    }
    return fallback
}

export const isPdfStampPending = report =>
    !!report &&
    report.status === APPROVAL_STATUS.Approved &&
    !report.approvedPdfUrl

const resolveWorkflowStatus = (item, isApproved) => {
    const raw = String(
        firstDefined(
            item.WorkflowStatus,
            item.workflowStatus,
            item.QueueStatus,
            item.queueStatus,
            '',
        ),
    ).toLowerCase()

    if (
        raw === APPROVAL_STATUS.AwaitingMe ||
        raw === 'pending_you' ||
        raw === 'pending you'
    ) {
        return APPROVAL_STATUS.AwaitingMe
    }
    if (
        raw === APPROVAL_STATUS.AwaitingOthers ||
        raw === 'awaiting_other' ||
        raw === 'waiting'
    ) {
        return APPROVAL_STATUS.AwaitingOthers
    }
    if (raw === APPROVAL_STATUS.Rejected || raw === 'rejected') {
        return APPROVAL_STATUS.Rejected
    }
    if (isApproved) return APPROVAL_STATUS.Approved
    return APPROVAL_STATUS.Pending
}

export const normalizeApprovalReport = item => {
    if (!item || typeof item !== 'object') return null

    const completedReportId = firstDefined(
        item.CompletedReportId,
        item.completedReportId,
        item.E_CompletedReportId,
        item.CompletedReportID,
        item.Id,
        item.id,
    )

    const approvalStatusRaw = firstDefined(
        item.ApprovalStatus,
        item.approvalStatus,
        item.ReportApprovalStatus,
        item.Status,
        item.status,
        'pending',
    )

    const approvalStatus = String(approvalStatusRaw).toLowerCase()
    const isApproved =
        approvalStatus === 'approved' ||
        approvalStatus === '2' ||
        item.IsApproved === true ||
        item.isApproved === true ||
        !!firstDefined(
            item.ApprovedAt,
            item.approvedAt,
            item.ApprovedOnUtc,
            item.approvedOnUtc,
            item.ApprovalDate,
        )

    const customerName = firstDefined(
        item.CustomerName,
        item.customerName,
        item.ClientName,
        item.clientName,
        item.SiteName,
        item.siteName,
        item.LocationName,
        item.StoreName,
        item.Customer,
        item.customer,
    )

    const auditorName = firstDefined(
        item.AuditorName,
        item.auditorName,
        item.AuditorEmail,
        item.auditorEmail,
        item.CreatedByName,
        item.createdByName,
        item.InspectedBy,
        item.inspectedBy,
        item.InspectorName,
        item.inspectorName,
        item.UserName,
        item.userName,
        item.CreatedBy,
        item.createdBy,
        item.OwnerName,
    )

    const completedAt = firstDefined(
        item.SubmittedOnUtc,
        item.submittedOnUtc,
        item.CompletedAt,
        item.completedAt,
        item.CompletedDate,
        item.completedDate,
        item.DateCompleted,
        item.AuditDate,
        item.auditDate,
        item.InspectionDate,
        item.inspectionDate,
        item.SubmittedAt,
        item.submittedAt,
        item.EndDate,
        item.endDate,
        item.ReportDate,
        item.reportDate,
        item.CreatedDate,
        item.createdDate,
        item.ModifiedDate,
    )

    return {
        Id: completedReportId,
        completedReportId,
        auditId: firstDefined(item.AuditId, item.auditId),
        auditName: firstDefined(item.AuditName, item.auditName, '') || '',
        templateId: firstDefined(item.TemplateId, item.templateId),
        templateName: firstDefined(
            item.TemplateName,
            item.templateName,
            item.AuditName,
            item.auditName,
            item.ReportName,
            'Untitled report',
        ),
        customerId: firstDefined(item.CustomerId, item.customerId, item.ClientId),
        customerName: customerName || '',
        auditorName: auditorName || '',
        auditorEmail:
            firstDefined(item.AuditorEmail, item.auditorEmail, '') || '',
        auditorId: firstDefined(
            item.AuditorUserId,
            item.auditorUserId,
            item.UserId,
            item.userId,
            item.AuditorId,
            item.auditorId,
            item.CreatedById,
        ),
        completedAt: completedAt || null,
        score: firstDefined(item.Score, item.score, item.TotalScore),
        questionsPassed: firstDefined(
            item.QuestionsPassed,
            item.questionsPassed,
        ),
        questionsTotal: firstDefined(item.QuestionsTotal, item.questionsTotal),
        status: isApproved ? APPROVAL_STATUS.Approved : APPROVAL_STATUS.Pending,
        pdfUrl:
            firstDefined(
                item.OriginalPdfUrl,
                item.originalPdfUrl,
                item.PdfUrl,
                item.pdfUrl,
                item.ReportPdfUrl,
                item.reportPdfUrl,
                item.ReportPath,
                item.reportPath,
                item.DownloadPDFLInk,
                item.DownloadPDFLink,
                item.FilePath,
            ) || '',
        approvedBy:
            firstDefined(
                item.ApprovedByName,
                item.approvedByName,
                item.ApprovedBy,
                item.approvedBy,
                item.ApproverName,
            ) || '',
        approvedAt: firstDefined(
            item.ApprovedOnUtc,
            item.approvedOnUtc,
            item.ApprovedAt,
            item.approvedAt,
            item.ApprovalDate,
            item.approvalDate,
        ),
        approvedPdfUrl:
            firstDefined(
                item.ApprovedPdfUrl,
                item.approvedPdfUrl,
                item.ApprovedReportUrl,
                item.approvedReportUrl,
                item.ApprovedReportPath,
            ) || '',
        signatureUrl:
            firstDefined(
                item.ApprovalSignatureUrl,
                item.approvalSignatureUrl,
                item.SignatureUrl,
                item.signatureUrl,
            ) || '',
        signatureText:
            firstDefined(
                item.ApprovalSignatureText,
                item.approvalSignatureText,
                item.SignatureText,
            ) || '',
        signatureType: firstDefined(
            item.ApprovalSignatureType,
            item.approvalSignatureType,
            item.SignatureType,
        ),
        notes:
            firstDefined(
                item.ApprovalNotes,
                item.approvalNotes,
                item.Notes,
                item.notes,
            ) || '',
        notificationSent: parseBool(
            firstDefined(
                item.ApprovalNotificationSent,
                item.approvalNotificationSent,
                item.NotificationSent,
                item.notificationSent,
            ),
            false,
        ),
        locationName:
            firstDefined(
                item.LocationName,
                item.locationName,
                item.SiteName,
                item.siteName,
            ) || '',
        waitingOn:
            firstDefined(
                item.WaitingOn,
                item.waitingOn,
                item.WaitingOnName,
                item.waitingOnName,
            ) || '',
        currentStep: firstDefined(
            item.CurrentStep,
            item.currentStep,
            item.StepNumber,
            item.stepNumber,
        ),
        totalSteps: firstDefined(
            item.TotalSteps,
            item.totalSteps,
            item.ApproverCount,
            item.approverCount,
        ),
        ageLabel:
            firstDefined(item.AgeLabel, item.ageLabel, item.Age, item.age) ||
            '',
        rejectedReason:
            firstDefined(
                item.RejectedReason,
                item.rejectedReason,
                item.RejectionReason,
                item.rejectionReason,
            ) || '',
        nextApproverName:
            firstDefined(
                item.NextApproverName,
                item.nextApproverName,
            ) || '',
        isCurrentApprover: parseBool(
            firstDefined(
                item.IsCurrentApprover,
                item.isCurrentApprover,
                item.CanAdvance,
                item.canAdvance,
            ),
            false,
        ),
        hasWorkflow: parseBool(
            firstDefined(
                item.HasApprovalWorkflow,
                item.hasApprovalWorkflow,
                item.HasWorkflow,
                item.hasWorkflow,
            ),
            false,
        ),
        canReject: parseBool(
            firstDefined(item.CanReject, item.canReject),
            false,
        ),
        workflowStatus: resolveWorkflowStatus(item, isApproved),
    }
}

export const normalizeApprovalListResponse = response => {
    const root = response?.Data ?? response?.data ?? response ?? {}
    const itemsRaw = firstDefined(
        root.Items,
        root.items,
        root.Reports,
        root.reports,
        root.PendingReports,
        root.pendingReports,
        Array.isArray(root) ? root : null,
        [],
    )

    const items = (Array.isArray(itemsRaw) ? itemsRaw : [])
        .map(normalizeApprovalReport)
        .filter(Boolean)

    const counters = root.Counters ?? root.counters ?? {}
    const meta = root.MetaData ?? root.metaData ?? root.Pagination ?? {}

    return {
        items,
        totalCount:
            firstDefined(
                root.TotalCount,
                root.totalCount,
                meta.Total,
                meta.total,
                items.length,
            ) || 0,
        counters: {
            pending:
                firstDefined(
                    counters.Pending,
                    counters.pending,
                    root.PendingCount,
                    root.pendingCount,
                    0,
                ) || 0,
            approvedThisMonth:
                firstDefined(
                    counters.ApprovedThisMonth,
                    counters.approvedThisMonth,
                    root.ApprovedThisMonthCount,
                    root.approvedThisMonthCount,
                    root.ApprovedCount,
                    0,
                ) || 0,
        },
        meta,
    }
}

/** Fill empty AuditorName from UserDetails list using AuditorUserId. */
export const enrichApprovalReportsWithUsers = (items, userOptions = []) => {
    if (!items?.length) return items || []
    if (!userOptions?.length) return items

    const byId = userOptions.reduce((acc, opt) => {
        if (opt?.value != null) {
            acc[String(opt.value)] = opt.text || opt.UserName || ''
        }
        return acc
    }, {})

    return items.map(item => {
        if (item.auditorName) return item
        const resolved = byId[String(item.auditorId)]
        if (!resolved) return item
        return { ...item, auditorName: resolved }
    })
}

/**
 * Apply list filters on the client when the API ignores/partially supports them.
 * PendingReports returns AuditorUserId + TemplateName (often no CustomerId).
 */
export const applyApprovalClientFilters = (
    items,
    filter = {},
    templateOptions = [],
) => {
    let result = Array.isArray(items) ? [...items] : []

    if (filter.UserId) {
        const auditorId = String(filter.UserId)
        result = result.filter(item => String(item.auditorId) === auditorId)
    }

    if (filter.TemplateId) {
        const selected = (templateOptions || []).find(
            opt => String(opt.value) === String(filter.TemplateId),
        )
        const templateName = selected?.text
        result = result.filter(item => {
            if (
                item.templateId != null &&
                String(item.templateId) === String(filter.TemplateId)
            ) {
                return true
            }
            if (!templateName) return false
            return (
                String(item.templateName || '').toLowerCase() ===
                String(templateName).toLowerCase()
            )
        })
    }

    if (filter.CustomerId) {
        const customerId = String(filter.CustomerId)
        // Only filter when the API includes customerId on the row
        const hasAnyCustomerId = result.some(item => item.customerId != null)
        if (hasAnyCustomerId) {
            result = result.filter(
                item => String(item.customerId) === customerId,
            )
        }
    }

    if (filter.StartDate) {
        const start = new Date(filter.StartDate).getTime()
        if (!Number.isNaN(start)) {
            result = result.filter(item => {
                if (!item.completedAt) return false
                return new Date(item.completedAt).getTime() >= start
            })
        }
    }

    if (filter.EndDate) {
        const end = new Date(filter.EndDate).getTime()
        if (!Number.isNaN(end)) {
            result = result.filter(item => {
                if (!item.completedAt) return false
                return new Date(item.completedAt).getTime() <= end
            })
        }
    }

    if (filter.Search) {
        const q = String(filter.Search).trim().toLowerCase()
        if (q) {
            result = result.filter(item => {
                const haystack = [
                    item.auditName,
                    item.templateName,
                    item.auditorName,
                    item.auditorEmail,
                    item.customerName,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                return haystack.includes(q)
            })
        }
    }

    return result
}

export const normalizeApprovalDetail = response => {
    const root = response?.Data ?? response?.data ?? response ?? {}
    const reportSource = firstDefined(root.Report, root.report, root) || {}
    const report = normalizeApprovalReport(reportSource)
    if (!report) return null

    const canApprove = firstDefined(root.CanApprove, root.canApprove, reportSource.CanApprove)
    const reason =
        firstDefined(root.Reason, root.reason, reportSource.Reason, reportSource.reason) ||
        ''

    return {
        ...report,
        canApprove:
            typeof canApprove === 'boolean'
                ? canApprove
                : report.status !== APPROVAL_STATUS.Approved,
        canApproveReason: reason,
        canReject: parseBool(
            firstDefined(
                root.CanReject,
                root.canReject,
                reportSource.CanReject,
                report.canReject,
            ),
            false,
        ),
        isCurrentApprover: parseBool(
            firstDefined(
                root.IsCurrentApprover,
                root.isCurrentApprover,
                reportSource.IsCurrentApprover,
                report.isCurrentApprover,
            ),
            false,
        ),
        nextApproverName:
            firstDefined(
                root.NextApproverName,
                root.nextApproverName,
                reportSource.NextApproverName,
                report.nextApproverName,
            ) || '',
        hasWorkflow: parseBool(
            firstDefined(
                root.HasApprovalWorkflow,
                root.hasApprovalWorkflow,
                reportSource.HasApprovalWorkflow,
                report.hasWorkflow,
            ),
            false,
        ),
        // no-login approval link (public/Approval/Sign/{token})
        linkExpiresOn:
            firstDefined(root.LinkExpiresOnUtc, root.linkExpiresOnUtc) || null,
    }
}

export const normalizeCanApproveResponse = response => {
    const root = response?.Data ?? response?.data ?? response ?? {}
    return {
        canApprove: !!(root.CanApprove ?? root.canApprove),
        reason: root.Reason ?? root.reason ?? '',
        approvedBy: root.ApprovedByName ?? root.approvedByName ?? '',
        approvedAt: root.ApprovedAt ?? root.approvedAt ?? null,
    }
}

export const stripBase64Prefix = value => {
    if (!value || typeof value !== 'string') return ''
    const commaIndex = value.indexOf(',')
    if (value.startsWith('data:') && commaIndex >= 0) {
        return value.slice(commaIndex + 1)
    }
    return value.trim()
}

export const buildApprovalPayload = ({
    notes,
    signatureType,
    signatureBase64,
    signatureText,
    notifyAuditor = false,
}) => {
    const payload = {
        Notes: notes?.trim() || '',
        SignatureType: signatureType,
        NotifyAuditor: !!notifyAuditor,
    }

    if (signatureType === SIGNATURE_TYPE.Typed) {
        payload.SignatureText = signatureText?.trim() || ''
    } else {
        payload.SignatureBase64 = stripBase64Prefix(signatureBase64)
    }

    return payload
}

export const extractApiError = error => {
    const data = error?.error ?? error?.response?.data ?? error
    if (typeof data === 'string' && data.trim()) return data
    const modelState = data?.ModelState ?? data?.modelState
    if (modelState && typeof modelState === 'object') {
        const first = Object.values(modelState).flat().find(Boolean)
        if (first) return String(first)
    }
    if (data?.errors && typeof data.errors === 'object') {
        const first = Object.values(data.errors).flat().find(Boolean)
        if (first) return String(first)
    }
    const message =
        data?.Message ??
        data?.message ??
        data?.ErrorMessage ??
        data?.errorMessage ??
        data?.Reason ??
        data?.reason ??
        data?.title
    if (message) return message
    if (error?.status === 0 || (error && error.status == null && error.error == null)) {
        return 'Save was blocked before the API responded (CORS / network).'
    }
    if (error?.status) return `Request failed (${error.status})`
    return 'Something went wrong. Please try again.'
}

export const formatApprovalDate = value => {
    if (!value) return '—'
    try {
        const date = value instanceof Date ? value : new Date(value)
        if (Number.isNaN(date.getTime())) return String(value)
        return DateUtility.dateToLocalISO(date, DateFormat.dateTime)
    } catch {
        return String(value)
    }
}

export const getApprovalConflictMessage = error => {
    const data = error?.error ?? error?.response?.data ?? error ?? {}
    const approvedBy =
        data.ApprovedByName ??
        data.approvedByName ??
        data.ApprovedBy ??
        data.approvedBy ??
        'another manager'
    const approvedAt = data.ApprovedAt ?? data.approvedAt
    if (approvedAt) {
        return `Already approved by ${approvedBy} on ${formatApprovalDate(approvedAt)}`
    }
    return `Already approved by ${approvedBy}`
}

/** Map draft UI filters into API filter payload (status pills stay separate). */
export const buildApprovalListFilter = (draft, status) => {
    let startDate = ''
    let endDate = ''

    if (draft.StartDate) {
        try {
            startDate = DateUtility.dateToLocalISO(
                DateUtility.startDay(new Date(draft.StartDate)),
            )
        } catch {
            startDate = ''
        }
    }

    if (draft.EndDate) {
        try {
            endDate = DateUtility.dateToLocalISO(
                DateUtility.endDay(new Date(draft.EndDate)),
            )
        } catch {
            endDate = ''
        }
    }

    return {
        CustomerId: draft.CustomerId || '',
        TemplateId: draft.TemplateId || '',
        UserId: draft.UserId || '',
        StartDate: startDate,
        EndDate: endDate,
        Search: draft.Search || '',
        status: status || APPROVAL_STATUS.Pending,
        PageNumber: 1,
    }
}

export const validateApprovalNotes = (notes, { required = true } = {}) => {
    const value = notes?.trim() || ''
    if (!required && !value) return ''
    if (required && value.length < MIN_APPROVAL_NOTES_LENGTH) {
        return `Notes must be at least ${MIN_APPROVAL_NOTES_LENGTH} characters.`
    }
    return ''
}

export const normalizeHistoryEvent = item => {
    if (!item || typeof item !== 'object') return null
    const statusRaw = String(
        firstDefined(item.Status, item.status, item.EventType, item.eventType, ''),
    ).toLowerCase()

    let kind = 'pending'
    if (
        statusRaw.includes('submit') ||
        statusRaw.includes('complete') ||
        statusRaw === 'submitted'
    ) {
        kind = 'submitted'
    } else if (statusRaw.includes('reject')) {
        kind = 'rejected'
    } else if (
        statusRaw.includes('approv') ||
        statusRaw.includes('final') ||
        statusRaw.includes('stamp') ||
        statusRaw.includes('pdf')
    ) {
        kind = 'approved'
    } else if (statusRaw.includes('wait') || statusRaw.includes('pending')) {
        kind = 'pending'
    }

    return {
        id: firstDefined(item.Id, item.id, item.StepOrder, item.stepOrder),
        title:
            firstDefined(
                item.Title,
                item.title,
                item.ApproverName,
                item.approverName,
                item.Name,
                item.name,
                item.EventType,
                item.eventType,
            ) || 'Approval step',
        role:
            firstDefined(
                item.Role,
                item.role,
                item.ApproverRole,
                item.approverRole,
            ) || '',
        actorName:
            firstDefined(
                item.ApproverName,
                item.approverName,
                item.ActorName,
                item.actorName,
                item.Name,
            ) || '',
        at: firstDefined(
            item.OccurredOnUtc,
            item.occurredOnUtc,
            item.ActedOnUtc,
            item.actedOnUtc,
            item.ApprovedOnUtc,
            item.approvedOnUtc,
            item.CreatedOnUtc,
            item.createdOnUtc,
            item.Timestamp,
        ),
        notes:
            firstDefined(
                item.Notes,
                item.notes,
                item.ApprovalNotes,
                item.approvalNotes,
                item.Reason,
                item.RejectionReason,
                item.rejectionReason,
            ) || '',
        signatureUrl:
            firstDefined(
                item.SignatureUrl,
                item.signatureUrl,
                item.ApprovalSignatureUrl,
            ) || '',
        signatureText:
            firstDefined(
                item.SignatureText,
                item.signatureText,
                item.ApprovalSignatureText,
            ) || '',
        isFinal: parseBool(
            firstDefined(item.IsFinal, item.isFinal, item.IsFinalApprover, item.isFinalApprover),
            false,
        ),
        isCurrent: parseBool(
            firstDefined(item.IsCurrent, item.isCurrent, item.IsWaiting, item.isWaiting),
            false,
        ),
        kind,
        status: statusRaw,
    }
}

export const normalizeApprovalHistory = response => {
    const root = response?.Data ?? response?.data ?? response ?? {}
    const itemsRaw = firstDefined(
        // GET Approval/Report/{id}/History (and the public one) answer { OverallStatus, CurrentStep, Events: [...] }
        root.Events,
        root.events,
        root.Items,
        root.items,
        root.History,
        root.history,
        root.Chain,
        root.chain,
        root.Steps,
        root.steps,
        Array.isArray(root) ? root : null,
        [],
    )
    return (Array.isArray(itemsRaw) ? itemsRaw : [])
        .map(normalizeHistoryEvent)
        .filter(Boolean)
}

const normalizeWorkflowApprover = (item, index) => {
    if (!item || typeof item !== 'object') return null
    return {
        id:
            firstDefined(
                item.Id,
                item.id,
                item.ApproverUserId,
                item.UserId,
                item.userId,
                `tmp-${index}`,
            ) || `tmp-${index}`,
        // GET Template/{id}/ApprovalWorkflow returns ApproverTypeId (1 user, 9 e-mail), ApproverUserId,
        // ApproverEmail, ApproverDisplayName, IsFinalApprover
        userId: firstDefined(item.ApproverUserId, item.UserId, item.userId),
        name:
            firstDefined(
                item.ApproverDisplayName,
                item.Name,
                item.name,
                item.UserName,
                item.userName,
                item.DisplayName,
            ) || '',
        email:
            firstDefined(item.ApproverEmail, item.Email, item.email, item.UserEmail) ||
            '',
        role: firstDefined(item.Role, item.role, item.Title, item.title) || '',
        isExternal:
            item.ApproverTypeId != null
                ? Number(item.ApproverTypeId) === 9
                : parseBool(
                      firstDefined(item.IsExternal, item.isExternal),
                      !firstDefined(item.UserId, item.userId),
                  ),
        isFinal: parseBool(
            firstDefined(item.IsFinalApprover, item.IsFinal, item.isFinal),
            false,
        ),
        stepOrder: Number(firstDefined(item.StepOrder, item.stepOrder, index + 1)),
    }
}

export const defaultWorkflowConfig = () => ({
    enabled: true,
    triggerType: WORKFLOW_TRIGGER.Immediate,
    delayValue: 24,
    delayUnit: 'hours',
    approvers: [],
    rejectionPolicy: 'anyone',
    reminderCadence: 'every_2_days_3',
    autoEscalate: 'after_5_days',
    regeneratePdf: true,
    includeAuditorOnNotifications: true,
    allowDelegate: false,
})

export const normalizeWorkflowConfig = response => {
    const root = response?.Data ?? response?.data ?? response ?? {}
    const source = firstDefined(root.Config, root.config, root) || {}
    const triggerRaw = firstDefined(
        source.TriggerType,
        source.triggerType,
        source.Trigger,
        source.trigger,
        WORKFLOW_TRIGGER.Delay,
    )
    let triggerType = WORKFLOW_TRIGGER.Delay
    if (triggerRaw === WORKFLOW_TRIGGER.Immediate || triggerRaw === 'immediate') {
        triggerType = WORKFLOW_TRIGGER.Immediate
    } else if (triggerRaw === WORKFLOW_TRIGGER.Manual || triggerRaw === 'manual') {
        triggerType = WORKFLOW_TRIGGER.Manual
    } else if (triggerRaw === WORKFLOW_TRIGGER.Delay || triggerRaw === 'delay') {
        triggerType = WORKFLOW_TRIGGER.Delay
    } else if (triggerRaw === WORKFLOW_TRIGGER.AfterFindings || triggerRaw === 'after_findings') {
        triggerType = WORKFLOW_TRIGGER.AfterFindings
    }
    // a template without a saved workflow (ConfigId 0): the server's default is Delay, but only "Immediately" is
    // applied today (Delay starts at completion like Immediately, Manual is not started) - start new ones there
    if (!Number(firstDefined(source.ConfigId, source.configId, 0))) {
        triggerType = WORKFLOW_TRIGGER.Immediate
    }

    const delayMinutes = firstDefined(source.TriggerDelayMinutes, source.triggerDelayMinutes)
    const delayHours = Number(
        firstDefined(
            source.DelayHours,
            source.delayHours,
            source.DelayValue,
            delayMinutes != null ? Number(delayMinutes) / 60 : null,
            24,
        ),
    )
    const isNewConfig = !Number(firstDefined(source.ConfigId, source.configId, 0))
    const delayUnit =
        firstDefined(source.DelayUnit, source.delayUnit, 'hours') === 'days'
            ? 'days'
            : 'hours'

    const approversRaw = firstDefined(
        source.Approvers,
        source.approvers,
        source.Chain,
        source.chain,
        [],
    )

    const defaults = defaultWorkflowConfig()
    return {
        ...defaults,
        // a template without a saved workflow comes back IsEnabled = false; the form starts switched on
        enabled: isNewConfig
            ? true
            : parseBool(
                  firstDefined(source.IsEnabled, source.Enabled, source.enabled),
                  true,
              ),
        triggerType,
        delayValue:
            delayUnit === 'days' ? Math.max(1, delayHours / 24) : delayHours || 24,
        delayUnit,
        approvers: (Array.isArray(approversRaw) ? approversRaw : [])
            .map(normalizeWorkflowApprover)
            .filter(Boolean),
        rejectionPolicy:
            Number(
                firstDefined(
                    source.RejectionPolicy,
                    source.rejectionPolicy,
                ),
            ) === REJECTION_POLICY.FinalOnly ||
            String(
                firstDefined(
                    source.RejectionPolicy,
                    source.rejectionPolicy,
                    '',
                ),
            ).toLowerCase() === 'final_only'
                ? 'final_only'
                : 'anyone',
        reminderCadence: (() => {
            const raw = firstDefined(
                source.ReminderCadence,
                source.reminderCadence,
                'every_2_days_3',
            )
            if (raw === REMINDER_CADENCE.Off || raw === 'off') return 'off'
            if (raw === REMINDER_CADENCE.Daily || raw === 'daily') return 'daily'
            return 'every_2_days_3'
        })(),
        autoEscalate: (() => {
            if (!isNewConfig && 'AutoEscalateAfterDays' in source) {
                return source.AutoEscalateAfterDays ? 'after_5_days' : 'off'
            }
            const raw = firstDefined(
                source.AutoEscalate,
                source.autoEscalate,
                'after_5_days',
            )
            if (raw === AUTO_ESCALATE.Off || raw === 'off') return 'off'
            return 'after_5_days'
        })(),
        regeneratePdf: parseBool(
            firstDefined(
                source.RegeneratePdfOnFinal,
                source.regeneratePdfOnFinal,
                source.RegeneratePdf,
            ),
            true,
        ),
        includeAuditorOnNotifications: parseBool(
            firstDefined(
                source.IncludeAuditorInNotifications,
                source.IncludeAuditorOnNotifications,
                source.includeAuditorOnNotifications,
            ),
            true,
        ),
        allowDelegate: parseBool(
            firstDefined(
                source.AllowDelegation,
                source.AllowDelegate,
                source.allowDelegate,
            ),
            false,
        ),
    }
}

// Body of PUT Template/{id}/ApprovalWorkflow - the server's ApprovalWorkflowConfigSaveRequest (it rebuilds the whole
// config + approver list from it). rawResponse (the earlier GET) is only used for values the form does not edit.
export const buildWorkflowPayload = (config, rawResponse) => {
    const root =
        rawResponse && typeof rawResponse === 'object' && !Array.isArray(rawResponse)
            ? rawResponse.Data ?? rawResponse.data ?? rawResponse
            : {}
    const existing = (root && (root.Config ?? root.config ?? root)) || {}
    const delayHours =
        config.delayUnit === 'days'
            ? Number(config.delayValue || 0) * 24
            : Number(config.delayValue || 0)
    const triggerType = Number(config.triggerType) || WORKFLOW_TRIGGER.Immediate

    const toUserId = value => {
        if (value == null || value === '') return null
        const numeric = Number(value)
        return Number.isNaN(numeric) ? null : numeric
    }

    const list = config.approvers || []
    const approvers = list.map((item, index) => {
        const external = !!item.isExternal
        return {
            StepOrder: index + 1,
            IsFinalApprover: index === list.length - 1,
            ApproverTypeId: external ? 9 : 1,
            ApproverUserId: external ? null : toUserId(item.userId),
            ApproverEmail: item.email || null,
            ApproverDisplayName: item.name || null,
        }
    })

    let reminderCadence = REMINDER_CADENCE.Every2Days3
    if (config.reminderCadence === 'off') reminderCadence = REMINDER_CADENCE.Off
    if (config.reminderCadence === 'daily') reminderCadence = REMINDER_CADENCE.Daily
    if (
        reminderCadence === REMINDER_CADENCE.Every2Days3 &&
        Number(existing.ReminderCadence) === 3
    ) {
        reminderCadence = 3
    }

    return {
        IsEnabled: !!config.enabled,
        TriggerType: triggerType,
        TriggerDelayMinutes:
            triggerType === WORKFLOW_TRIGGER.Delay
                ? Math.max(1, Math.round(delayHours * 60))
                : null,
        RejectionPolicy:
            config.rejectionPolicy === 'final_only'
                ? REJECTION_POLICY.FinalOnly
                : REJECTION_POLICY.Anyone,
        ReminderCadence: reminderCadence,
        ReminderMaxCount:
            reminderCadence === REMINDER_CADENCE.Off
                ? null
                : existing.ReminderMaxCount ?? 3,
        AutoEscalateAfterDays:
            config.autoEscalate === 'off'
                ? null
                : existing.AutoEscalateAfterDays || 5,
        RegeneratePdfOnFinal: !!config.regeneratePdf,
        IncludeAuditorInNotifications: !!config.includeAuditorOnNotifications,
        AllowDelegation: false,
        Approvers: approvers,
    }
}

export const buildRejectPayload = ({ reason, category }) => ({
    Reason: reason?.trim() || '',
    Category: category || 'other',
})
